<template>
  <div>
    <b-tabs
      card pills lazy
      :vertical="isWindowSM"
      v-model="currentTab"
      @activate-tab="tabChanged"
      nav-wrapper-class="bg-white"
      nav-class="bg-white"
    >
      <b-tab title="Prizes">
          <advanced-form
            autocomplete="off"
            layout="inline-md-3"
            submit-label="Save Leaderboard"
            v-bind="formFields"
            v-model="form"
            :disabled="viewOnly"
            @submit="input()"
          >
              <template v-slot:prizes.first.title-before>
                <hr/>
                <b-form-group :label="form.raffleMode ? 'Leaderboard Prize 🏆' : 'Leaderboard First Prize 🥇'"/>
              </template>
              <template v-slot:prizes.second.title-before>
                <hr/>
                <b-form-group :label="form.raffleMode ? 'Leaderboard Prize 🏆' : 'Leaderboard Second Prize 🥈'"/>
              </template>
              <template v-slot:prizes.third.title-before>
                <hr/>
                <b-form-group :label="form.raffleMode ? 'Leaderboard Prize 🏆' : 'Leaderboard Third Prize 🥉'"/>
              </template>
              <template v-if="viewOnly" v-slot:formControl>
                <div></div>
              </template>
          </advanced-form>
        </b-tab>
        <b-tab title="Score System">
          <div class="d-flex flex-column flex-fill">
            <b-card class="mb-4" v-for="(score, i) in scores" :key="`${i}-${score.id}`">
              <b-form-group label-cols="12" label-cols-md="3" label="Label (Required)" description="This name appears on the Leaderboard column header">
                <b-form-input v-model="score.label" :disabled="viewOnly" />
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Value (Required)"
                :description="leaderboard.raffleMode ? 'In raffle mode, these are the number of tickets awarded'
                  : 'In score mode, these are the number of points awarded'">
                <b-form-input required type="number" @mousewheel="e => e.preventDefault()" v-model="score.value" :disabled="viewOnly" />
              </b-form-group>
              <template v-if="score.event === 'JOB_SHARED:CONTENT_SHARED:JOB_BROWSE_LINKEDIN:JOB_SCHEDULE_SHARE_FIRST:CONTENT_SCHEDULE_SHARE_FIRST:EVENT_REFERRAL_CREATED:REFERRAL_SUGGESTED'">
                <b-form-group label-cols="12" label-cols-md="3" label="Daily Cap" description="The maximum amount of points a user can collect a day.">
                  <b-form-input type="number" v-model="score.dailyMaximum" :disabled="viewOnly" />
                </b-form-group>
                <b-form-group label-cols="12" label-cols-md="3" label="Weekly Cap" description="The maximum amount of points a user can collect a week.">
                  <b-form-input type="number" v-model="score.weeklyMaximum" :disabled="viewOnly" />
                </b-form-group>
              </template>
              <b-form-group label-cols="12" label-cols-md="3" label="Event">
                <b-form-select :name="`${score.id}-event`" :options="eventOptions" v-model="score.event" :disabled="viewOnly" />
              </b-form-group>
              <div v-if="!viewOnly" class="d-flex flex-row justify-content-end">
                <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(true, i)">Up</b-btn>
                <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(false, i)">Down</b-btn>
                <b-btn variant="outline-danger" @click="() => scores.splice(i, 1)">Remove</b-btn>
              </div>
            </b-card>
            <div v-if="!viewOnly" class="d-flex flex-row justify-content-end">
              <b-btn variant="outline-primary" @click="() => scores.push({ id: Date.now(), eventNames: [], value: 1 })" :disabled="viewOnly">Add New</b-btn>
            </div>
          </div>
          <div>
            <b-button variant="primary" @click="input()" v-if="!viewOnly">Save Leaderboard</b-button>
          </div>
        </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import CheckboxGroup from '@/components/settings/CheckboxGroup';
import Share from '@src/components/Share';
import _merge from 'lodash/merge';
import gridMixin from '@src/mixins/grid';
import { mapPreferences } from '@src/mixins/root';

export default {
  props: {
    value: {
      type: Object,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AdvancedForm,
    Share,
    CheckboxGroup,
  },
  data: () => ({
    form: {},
    scores: [],
    leaderboard: {},
    currentTab: null,
    initialForm: null,
  }),
  mixins: [
    gridMixin,
  ],
  computed: {
    ...mapPreferences([ 'canEmployeeApply' ]),
    eventOptions() {
      return [ {
        value: null,
        text: '--- Select an event ---'
      }, {
        value: 'JOB_SHARED:CONTENT_SHARED:JOB_BROWSE_LINKEDIN:JOB_SCHEDULE_SHARE_FIRST:CONTENT_SCHEDULE_SHARE_FIRST:EVENT_REFERRAL_CREATED:REFERRAL_SUGGESTED',
        text: 'Awarded when a job or piece of content is shared'
      }, {
        value: 'SHARE_UNIQUE_VISITED:CONTENT_SHARE_UNIQUE_VISITED',
        text: 'Awarded when a share link is viewed'
      }, {
        value: 'APPLICATION_CREATED:INTERNALAPPLICATION_CREATED',
        text: 'Awarded when a user\'s candidate applies for a role'
      }, {
        value: 'RECOMMENDATION_CREATED',
        text: 'Awarded when a user submits a recommendation for a candidate'
      }, {
        value: 'REFERRAL_FORM_SUBMITTED',
        text: 'Awarded when a user submits a questionnaire'
      }, {
        value: 'APPLICATION_CONFIRMED:INTERNALAPPLICATION_CONFIRMED',
        text: 'Awarded when a user\'s candidate application is confirmed'
      }, {
        value: 'APPLICATION_INTERVIEW:INTERNALAPPLICATION_INTERVIEW',
        text: 'Awarded when a user\'s candidate is moved to Interview'
      }, {
        value: 'APPLICATION_HIRED:INTERNALAPPLICATION_HIRED',
        text: 'Awarded when a user\'s candidate is moved to Hired'
      } ];
    },
    formFields() {
      const form = {
        raffleMode: {
          label: 'Raffle Mode',
          type: 'CHECKBOX',
          description: 'Enable Raffle Mode 🎟',
          helpText: 'Raffle Mode allows participants to collect tickets to increase their chances of winning',
        },
        ...[ 'prizes.first', 'prizes.second', 'prizes.third' ].reduce((list, key) => ({
          ...list,
          [`${key}.title`]: {
            label: '— Title',
            type: 'STRING',
            required: key === 'prizes.first',
            helpText: 'Leave this empty to remove this prize',
            validation: key === 'prizes.first' ? {
              minLength: 3,
            } : {},
          },
          [`${key}.image`]: {
            label: '— Image',
            type: 'FILE',
            helpText: 'Optionally include an image for the prize - for best results use a square image.',
            options: { type: 'IMAGE', accept: '.jpg,.jpeg,.gif,.png', showPreview: true },
          },
          [`${key}.description`]: {
            label: '— Description',
            type: 'TEXTAREA',
            helpText: 'Optionally write a description for this prize',
          },
          [`${key}.url`]: {
            label: '— URL',
            type: 'STRING',
            helpText: 'Optionally add a "Learn more" link to prizes',
            validation: {
              isURL: true,
            },
          },
          [`${key}.terms`]: {
            label: '— Terms',
            type: 'TEXTAREA',
            helpText: 'Optionally include any relevant Terms & Conditions',
          },
        }), {}),
      };

      return { form };
    },
    scoreIcons: () => ([
      'plug',
      'eye',
      'link',
      'file-alt',
      'users',
      'handshake',
    ]),
    scoreEventNames() {
      const eventNames = [
        {
          text: 'Application Created',
          value: 'APPLICATION_CREATED',
          tooltip: 'Awarded when a user\'s candidate applies for a role',
        },
        {
          text: 'Application Confirmed',
          value: 'APPLICATION_CONFIRMED',
          tooltip: 'Awarded when a user\'s candidate application is confirmed (this is only used when the application is submitted in your ATS after a redirect from Real Links.)',
        },
        {
          text: 'Application Interviewed',
          value: 'APPLICATION_INTERVIEW',
          tooltip: 'Awarded when a user\'s candidate is moved to Interview',
        },
        {
          text: 'Application Hired',
          value: 'APPLICATION_HIRED',
          tooltip: 'Awarded when a user\'s candidate is moved to Hired',
        },
        {
          text: 'Content Shared',
          value: 'CONTENT_SHARED',
          tooltip: 'Awarded when a user shares a content link (each time)',
        },
        {
          text: 'Content Scheduled',
          value: 'CONTENT_SCHEDULE_SHARE_FIRST',
          tooltip: 'Awarded when a user schedules a piece of content for sharing',
        },
        {
          text: 'Content Viewed',
          value: 'CONTENT_SHARE_VISITED',
          tooltip: 'Awarded when a visitor views content (each time)',
        },
        {
          text: 'Content Viewed (Unique)',
          value: 'CONTENT_SHARE_UNIQUE_VISITED',
          tooltip: 'Awarded every time when a new visitor views content',
        },
        {
          text: 'Content Viewed for the First Time',
          value: 'CONTENT_SHARE_FIRST_VISITED',
          tooltip: 'Awarded when the piece of content is visited for the first time',
        },
        {
          text: 'Job Shared',
          value: 'JOB_SHARED',
          tooltip: 'Awarded when a user shares a job (each time)',
        },
        {
          text: 'Job Scheduled',
          value: 'JOB_SCHEDULE_SHARE_FIRST',
          tooltip: 'Awarded when a user schedules a jobs for sharing',
        },
        {
          text: 'Job Viewed',
          value: 'SHARE_VISITED',
          tooltip: 'Awarded when a visitor views a share link (each time)',
        },
        {
          text: 'Job Viewed (Unique)',
          value: 'SHARE_UNIQUE_VISITED',
          tooltip: 'Awarded every time when a new visitor views a share link',
        },
        {
          text: 'Job Viewed for the First Time',
          value: 'SHARE_FIRST_VISITED',
          tooltip: 'Awarded when first visitor views a share link',
        },
        {
          text: 'Referral Suggested',
          value: 'REFERRAL_SUGGESTED',
          tooltip: 'Awarded when a user chooses to refer a contact',
        },
        {
          text: 'Jam Referral Suggested',
          value: 'EVENT_REFERRAL_CREATED',
          tooltip: 'Awarded when a user chooses to refer a contact during a referral jam',
        },
        {
          text: 'Referral Viewed',
          value: 'REFERRAL_VISITED',
          tooltip: 'Awarded when a visitor views a share link (each time)',
        },
        {
          text: 'Referral Viewed (Unique)',
          value: 'REFERRAL_UNIQUE_VISITED',
          tooltip: 'Awarded every time a new visitor views a share link',
        },
        {
          text: 'Referral Viewed for the First Time',
          value: 'REFERRAL_FIRST_VISITED',
          tooltip: 'Awarded when first visitor views a share link',
        },
        {
          text: 'LinkedIn Connections Browsed',
          value: 'JOB_BROWSE_LINKEDIN',
          tooltip: 'Awarded when a user searches for connections on LinkedIn (each time)',
        },
      ];
      return [
        ...eventNames,
        ...(this.canEmployeeApply ? [
          {
            text: 'Internal Application Created',
            value: 'INTERNALAPPLICATION_CREATED',
            tooltip: 'Awarded when a user applies for an internal role',
          },
          {
            text: 'Internal Application Confirmed',
            value: 'INTERNALAPPLICATION_CONFIRMED',
            tooltip: 'Awarded when a user\'s application is confirmed for an internal role (this is only used when the application is submitted in your ATS after a redirect from Real Links.)',
          },
          {
            text: 'Internal Application Interviewed',
            value: 'INTERNALAPPLICATION_INTERVIEW',
            tooltip: 'Awarded when a user\'s internal application is moved to Interview',
          },
          {
            text: 'Internal Application Hired',
            value: 'INTERNALAPPLICATION_HIRED',
            tooltip: 'Awarded when a user\'s internal application is moved to Hired',
          }
        ] : []),
      ];
    },
  },
  created() {
    const { leaderboard } = this.value;
    console.log({ leaderboard });
    this.leaderboard = { ...(leaderboard || {}) };
    this.scores = JSON.parse(JSON.stringify([ ...(Array.isArray(leaderboard.scores) ? leaderboard.scores : []) ])).map(el => {
      el.event = Array.isArray(el.eventNames) && el.eventNames.length ? el.eventNames.join(':') : null;
      delete el.__typename;
      return el;
    });
    Object.keys(this.formFields.form).forEach(key => {
      if (key.includes('.')) {
        this.form[key] = key.split('.').reduce((out, k, i) => {
          if (i === 0) {
            return leaderboard ? leaderboard[k] : null;
          }
          return out ? out[k] : null;
        }, null);
      } else {
        this.form[key] = leaderboard[key];
      }
    });
  },
  methods: {
    getIcon(key) {
      const iconMap = {
        'JOB_SHARED:CONTENT_SHARED:JOB_BROWSE_LINKEDIN:JOB_SCHEDULE_SHARE_FIRST:CONTENT_SCHEDULE_SHARE_FIRST:EVENT_REFERRAL_CREATED:REFERRAL_SUGGESTED': 'link',
        'SHARE_UNIQUE_VISITED:CONTENT_SHARE_UNIQUE_VISITED': 'eye',
        'APPLICATION_CREATED:INTERNALAPPLICATION_CREATED': 'file-alt',
        'APPLICATION_CONFIRMED:INTERNALAPPLICATION_CONFIRMED': 'file-alt',
        'APPLICATION_INTERVIEW:INTERNALAPPLICATION_INTERVIEW': 'users',
        'APPLICATION_HIRED:INTERNALAPPLICATION_HIRED': 'handshake',
      };
      return iconMap[key] || 'link';
    },
    getDescription(key) {
      // continue here
    },
    input() {
      console.log({ keys: Object.keys(this.form) });
      const component = { leaderboard: Object.keys(this.form).reduce((out, key) => {
        if (key.includes('.')) {
          const subKeys = key.split('.').reverse();
          const subTree = subKeys.reduce((o, k, i) => {
            if (i === 0) {
              if (k === 'image') {
                o[k] = this.form[key] ? { id: this.form[key].id } : null;
              } else {
                o[k] = this.form[key];
              }
              return o;
            }
            o[k] = JSON.parse(JSON.stringify(o));
            delete o[subKeys[i - 1]];
            return o;
          }, {});
          return _merge(out, subTree);
        }
        if (key === 'scores') {
          out[key] = [ ...(Array.isArray(this.scores) ? this.scores : []) ];
          return out;
        }
        out[key] = this.form[key];
        return out;
      }, {}) };
      component.leaderboard.scores = [ ...(Array.isArray(this.scores) ? this.scores : []) ].map(el => {
        const { event, ...rest } = el;
        rest.value = parseInt(rest.value);
        rest.dailyMaximum = parseInt(rest.dailyMaximum);
        rest.weeklyMaximum = parseInt(rest.weeklyMaximum);
        rest.eventNames = event ? event.split(':') : [];
        rest.icon = this.getIcon(event);
        return {
          ...rest,
        };
      });
      console.log(JSON.parse(JSON.stringify({ component })));

      // if no second or third prize, remove them
      if (component && component.leaderboard && component.leaderboard.prizes) {
        if (component.leaderboard.prizes.second && !component.leaderboard.prizes.second.title) {
          component.leaderboard.prizes.second = null;
        }
        if (component.leaderboard.prizes.third && !component.leaderboard.prizes.third.title) {
          component.leaderboard.prizes.third = null;
        }
      }

      this.$emit('input', component);
    },
    tabChanged(next, prev, e) {
      console.log({ next, prev, e });
      return true;
    },
    scoreMoveItem(up, i) {
      if (i === 0 && up) {
        return false;
      }
      if (i === this.scores.length - 1 && !up) {
        return false;
      }

      const j = up ? i - 1 : i + 1;
      const [ value ] = this.scores.splice(i, 1);
      this.scores.splice(j, 0, value);
    },
  }
};
</script>

<style lang="scss">
.referral-jam-event-settings {
  .event-logo {
    max-height: 30px;
    max-width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-leaderboard.vue